import { setWindowLocation } from '@eventbrite/http';
import { locationDuck } from '@eventbrite/redux-destination';
import { RouteComponentProps } from 'react-router-dom';
import {
    DiscoverState,
    LocationState,
    SearchState,
    ThingsToDoShelfState,
} from '../../types';
import {
    getSearchEventHeaderTitle,
    updateDocumentTitle,
} from '../../utils/general';
import {
    constructNormalizedSearchUrl,
    constructSearchUrl,
    getCurrentNormalizedUrl,
} from '../selectors/url';
import { updateSearchCriteria } from './search';
import { thingsToDoShelf } from './thingsToDoShelf';

const {
    actions: { updateLocation },
} = locationDuck;

export const STORE_EVENT_SEARCH_AND_LOCATION_BY_URL =
    'STORE_EVENT_SEARCH_AND_LOCATION_BY_URL';

/*
 * In order to restore previous eventSearch data only from url we need to store them in redux mapped by the url
 */
export const storeEventSearchAndLocationByUrl = (
    url: string,
    search: SearchState,
    location: LocationState,
    thingsToDoShelf: ThingsToDoShelfState,
) => ({
    type: STORE_EVENT_SEARCH_AND_LOCATION_BY_URL,
    payload: {
        url,
        search,
        location,
        thingsToDoShelf,
    },
});

/*
 * This function ensures the redux event search and the url are sync when the url changes
 * We are unable right now to generate a event search object from the url, so we need to sync both sources of truth.
 */
export const restoreEventSearchFromUrl =
    (history: RouteComponentProps['history']) =>
    (dispatch: Function, getState: Function) => {
        const { search, location, previousUrlContext, app }: DiscoverState =
            getState();

        // Fetch the current url
        const currentUrl = getCurrentNormalizedUrl(history);

        // Compute the url of the event search data
        const eventSearchUrl = constructNormalizedSearchUrl(
            search.eventSearch.page,
            search.eventSearch,
            location.slug,
        );

        // Url and event search are synced, nothing to do here
        if (currentUrl === eventSearchUrl) {
            return;
        }

        // Is the user returning to a previous page?
        const previousEventSearch = previousUrlContext[currentUrl];

        if (!previousEventSearch) {
            // This occurs when  the user has force a reload and then navigated to a previous page
            // Since we cannot infere the event search information from the URL we need to do a hard reload
            // This reload is seamless in production due to
            setWindowLocation(currentUrl);
        } else {
            // User is returning to a previous page
            dispatch(updateLocation(previousEventSearch.location));
            dispatch(
                updateSearchCriteria(
                    previousEventSearch.search.eventSearch,
                    previousEventSearch.search.gaTrackingData,
                ),
            );
            if (app.featureFlags && app.featureFlags.enableThingsToDoPhase2) {
                dispatch(thingsToDoShelf(previousEventSearch.thingsToDoShelf));
            }
        }
    };

/*
 * This function updates the url with the new event search and stores location / search in case user navigates back and forth
 * If the url is already updated we should do nothing
 * We are unable right now to generate a event search object from the url, so we need to sync both sources of truth.
 */
export const syncEventSearchAndUrl =
    (history: RouteComponentProps['history']) =>
    (dispatch: Function, getState: Function) => {
        const { search, location, thingsToDoShelf }: DiscoverState = getState();

        // Fetch the current url
        const currentUrl = getCurrentNormalizedUrl(history);

        // Compute the url of the event search data
        const eventSearchUrl = constructNormalizedSearchUrl(
            search.eventSearch.page,
            search.eventSearch,
            location.slug,
        );

        // Url and event search are synced, nothing to do here
        if (currentUrl === eventSearchUrl) {
            // This is to update thingsToDoShelf data after it is updated on completion of API call
            dispatch(
                storeEventSearchAndLocationByUrl(
                    eventSearchUrl,
                    search,
                    location,
                    thingsToDoShelf,
                ),
            );
            return;
        }

        // Store eventSearch data and navigate to the new url
        dispatch(
            storeEventSearchAndLocationByUrl(
                eventSearchUrl,
                search,
                location,
                thingsToDoShelf,
            ),
        );
        const nextUrl = constructSearchUrl(
            search.eventSearch.page,
            search.eventSearch,
            location.slug,
            history.location,
        );
        history.push(nextUrl);
        updateSearchDocumentTitle(location, search);
    };

function updateSearchDocumentTitle(
    { currentPlace, currentPlaceParent, placeType }: LocationState,
    { eventSearch }: SearchState,
) {
    // Update page title with new search query data
    updateDocumentTitle(
        getSearchEventHeaderTitle(
            { currentPlace, currentPlaceParent, placeType },
            { eventSearch },
            true,
        ),
    );
}
